import type { NavigateFunction } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Canonical process-menu options shared by every post-dashboard servicing
 * screen, in the exact order of the customer's Figma design
 * (`update-account-info-current-savings` and the sibling account/servicing
 * exports). This is the single source of truth — screens import it rather than
 * defining their own list, so the dropdown stays consistent across the app
 * (issue #45).
 *
 * The menu is a pure navigation chooser: the route each option points to stays
 * independently RBAC-gated (deny-by-default), so a menu entry can never bypass a
 * screen's permission gate. Any label without a wired route surfaces an honest
 * informational toast rather than navigating to a non-existent route.
 *
 * "View Customer Details" was removed from the menu on the product owner's
 * ruling (issue #264): it had no destination screen in this release, so it is no
 * longer offered rather than surfacing a "not yet available" toast.
 */
export const PROCESS_MENU_OPTIONS = [
  'Account History',
  'Call History',
  'Card Maintenance',
  'Change Customer Details',
  'Chequebook(s)',
  'Customer Dashboard',
  'Customer Notes',
  'High Risk Security',
  'Hold Information',
  'Internet Password',
  'Mobile Number Update',
  'Notice Information',
  'Outbound Security',
  'Payments',
  'Postal Address Information',
  'Statements',
  'Transaction History',
] as const;

/**
 * Build the shared process-menu `onSelect` handler for a screen.
 *
 * Wires each option to its existing, RBAC-gated route. Account-scoped
 * destinations carry the current `?account=` context forward when an account
 * number is in scope. Options whose screen is not built in this release surface
 * an informational toast — they never navigate to a non-existent route.
 *
 * @param navigate  react-router `useNavigate()` function.
 * @param accountNumber  optional in-scope account number to forward to
 *   account-scoped destinations (e.g. Hold Information).
 */
export function makeProcessMenuHandler(
  navigate: NavigateFunction,
  accountNumber = '',
): (option: string) => void {
  return function onProcessSelect(option: string): void {
    const accountQuery = accountNumber
      ? `?account=${encodeURIComponent(accountNumber)}`
      : '';
    switch (option) {
      case 'Account History':
        navigate(`/accounts/history${accountQuery}`);
        break;
      case 'Call History':
        navigate('/call-history');
        break;
      case 'Card Maintenance':
        navigate('/cards');
        break;
      case 'Change Customer Details':
        navigate('/personal-details');
        break;
      case 'Chequebook(s)':
        navigate('/chequebook');
        break;
      case 'Customer Dashboard':
        navigate('/dashboard');
        break;
      case 'Customer Notes':
        navigate('/customer-notes');
        break;
      case 'High Risk Security':
        // Matches the existing security screens' process-menu mapping.
        navigate('/security/visa-high');
        break;
      case 'Hold Information':
        navigate(`/accounts/holds${accountQuery}`);
        break;
      case 'Internet Password':
        navigate('/internet-password');
        break;
      case 'Mobile Number Update':
        navigate('/mobile-update');
        break;
      case 'Notice Information':
        navigate('/notice-information');
        break;
      case 'Outbound Security':
        // SCRN-11 (CCF-105). Navigable from the dashboard process menu per the
        // product owner (#486); the route stays independently RBAC-gated.
        navigate('/outbound-security');
        break;
      case 'Payments':
        navigate('/payments');
        break;
      case 'Postal Address Information':
        navigate('/postal-address');
        break;
      case 'Statements':
        navigate('/statements');
        break;
      case 'Transaction History':
        navigate('/transactions');
        break;
      default:
        // Defensive fallback: any label without a wired route is surfaced
        // honestly rather than navigating nowhere. Every current menu entry is
        // routed above, so this is not reached in normal use.
        toast.info(`${option} is not yet available.`);
    }
  };
}
